/**
 * @name ProductTile
 * @description Display a single product tile, small size.
 * @class
 *
 * @author James Norton <jamnorton@deloitte.com>
 * @param {Object} props
 * @param {Object} props.data - Product data
 * @example <ProductTile product={product} />
 */

// THIRD-PARTY
import * as React from "react";
import { PropTypes, shape, string, bool, number } from "prop-types";
import groupBy from "lodash/groupBy";
import isEqual from "lodash/isEqual";
import kebabCase from "lodash/kebabCase";
import Cookies from "js-cookie";
import {
  constructSizes,
  Pill,
  successIcon,
} from "@lululemon/ecom-pattern-library";
import { Price } from "@lululemon/ui-library";

/**
 * An automated solution for this product badge/highlight is in progress. After it's done,
 * this hard-coded config will be removed along with obsolete AB Test flags. See related tickets:
 * https://lululemon.atlassian.net/browse/COCO-3463, https://lululemon.atlassian.net/browse/COCO-2658
 */

// COMPONENTS
import SwatchCarousel from "../slider/swatch-carousel";
import HoverImage from "../hover-image/hover-image";
import WishlistHeart from "@lululemon/ecom-wishlist/src/components/wishlist-heart-container-cdp/wishlist-heart-container-cdp";

// UTILITIES
import {
  analyticsProductLinkDataAttributes,
  updateProductColorAndSkuId,
} from "../../lib/analytics";
import {
  colorGroupLength,
  uniqueSkuStyleOrder,
  insertLineBreakNoHTML,
  isSelfCareProductTile,
  constructImageUrls,
  constructSwatches,
  stripDecimalFromPrice,
} from "../../lib/tile-utils";
import { MAX_GRID_WIDTH, TABLET_GRID_BREAKPOINT } from "../../lib/breakpoints";
import LinkGenerator from "../../lib/link-generator";

// STYLES
import "./product-tile.scss";
import ProductHighlight, { HIGHLIGHT_TYPE } from "./product-highlight";
import SelectiveComparisonCheckbox from "@lululemon/ecom-selective-comparison/src/components/selective-comparison-checkbox/selective-comparison-checkbox";

const sizes = constructSizes([
  { breakpoint: MAX_GRID_WIDTH, slotSize: "312px" },
  { breakpoint: "991px", slotSize: "22vw" },
  { breakpoint: TABLET_GRID_BREAKPOINT, slotSize: "31vw" },
  { slotSize: "47vw" },
]);

class ProductTile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...this.buildImages, // TODO: initial in constructor or parent
      previewPhotoImageIndex: 0,
      selectedSwatchIndex: 0,
      selectedColor: "",
      isItemInWishlist: false,
    };

    // Once the state is initialized, update it to default to the first non-disabled swatch/image
    const firstNonDisabledSwatch = this.state.swatches.findIndex(
      ({ id }) => !this.disabledColorIds.has(id)
    );

    if (firstNonDisabledSwatch > 0) {
      this.state.previewPhotoImageIndex = firstNonDisabledSwatch;
      this.state.selectedSwatchIndex = firstNonDisabledSwatch;
    }

    this.selectSwatch = this.selectSwatch.bind(this);
    this.setSwatchToFilteredColorGroup =
      this.updateDefaultSwatchSelection.bind(this);
    this.createSizeQueryStr = this.createSizeQueryStr.bind(this);
    this.createSizeUrlQueryStringForColorId =
      this.createSizeUrlQueryStringForColorId.bind(this);

    this.isFrenchLocale = Cookies.get("UsrLocale") === "fr_CA";
    this.setIsItemInWishlist = this.setIsItemInWishlist.bind(this);
    this.openDrawerRef = React.createRef();
  }

  componentDidMount() {
    const { product } = this.props;
    // remove duplicate color swatches caused by white space
    const skuStyleOrder = uniqueSkuStyleOrder(product["sku-style-order"]);
    const filteredBopisSwatches = this.filterBopisSwatches(skuStyleOrder);

    this.updateDefaultSwatchSelection(filteredBopisSwatches);
  }

  componentDidUpdate(prevProps) {
    const { currentColorFilters, product, skus } = this.props;
    const colorFiltersChanged = !isEqual(
      prevProps.currentColorFilters,
      currentColorFilters
    );
    const skusChanged = !isEqual(prevProps.skus, skus);

    if (!(colorFiltersChanged || skusChanged)) {
      return;
    }
    // remove duplicate color swatches caused by white space
    const skuStyleOrder = uniqueSkuStyleOrder(product["sku-style-order"]);
    const filteredBopisSwatches = this.filterBopisSwatches(skuStyleOrder);

    this.updateDefaultSwatchSelection(filteredBopisSwatches);
  }

  updateDefaultSwatchSelection(products) {
    const { currentColorFilters, product } = this.props;
    const filteredColorGroup = currentColorFilters?.[0] ?? null;
    const availableProducts = products.filter(
      (product) => !this.disabledColorIds.has(product["color-id"])
    );

    const filteredColorGroupProduct =
      filteredColorGroup == null
        ? availableProducts[0]
        : availableProducts.find((product) =>
            product?.["color-group"]?.includes(filteredColorGroup)
          );

    // GET THE INDEX OF THE ARRAY ITEM THAT CONTAINS THE FILTERED COLOR GROUP
    const filteredColorGroupIndex = products.findIndex(
      (product) => product.sku === filteredColorGroupProduct?.sku
    );

    // IF THERE IS A FILTERED COLOR GROUP, SELECT IT
    if (filteredColorGroupIndex > -1) {
      this.selectSwatch(null, null, filteredColorGroupIndex);
    }
  }

  // BUILD LIST OF IMAGES & SWATCHES FROM SKU-STYLE-ORDER
  get buildImages() {
    const { product } = this.props;
    // remove duplicate color swatches caused by white space
    const skuStyleOrder = uniqueSkuStyleOrder(product["sku-style-order"]);
    const filteredBopisSwatches = this.filterBopisSwatches(skuStyleOrder);
    const images = constructImageUrls(filteredBopisSwatches);
    const swatches = constructSwatches(filteredBopisSwatches);

    return {
      images,
      swatches,
      selectedSwatchId: skuStyleOrder[0]?.["color-id"] || null,
    };
  }

  get disabledColorIds() {
    const { swatches } = this.state;
    const { currentSizeFilters, skus } = this.props;

    // Return an empty set if no skus are provided to this component (nothing disabled)
    if (!skus) return new Set();

    const currentSizeFiltersNoWs = currentSizeFilters.map((size) =>
      size.replace(/\s/g, "")
    );
    const skusMatchingSize =
      currentSizeFiltersNoWs.length > 0
        ? skus.filter(({ size }) =>
            currentSizeFiltersNoWs.includes(size.replace(/\s/g, ""))
          )
        : skus;
    const availableColorIds = new Set(
      skusMatchingSize.map((sku) => sku.colorId)
    );

    return new Set(
      swatches
        .map((swatch) => swatch.id)
        .filter((id) => !availableColorIds.has(id))
    );
  }

  filterBopisSwatches(skuStyleOrder) {
    const { showBopis } = this.props;
    if (showBopis) {
      return skuStyleOrder.filter((swatch) => !!swatch["in-store"]);
    }
    return skuStyleOrder;
  }

  selectSwatch(e, value, index) {
    const { product } = this.props;

    // remove duplicate color swatches caused by white space
    const skuStyleOrder = uniqueSkuStyleOrder(product["sku-style-order"]);
    const filteredBopisSwatches = this.filterBopisSwatches(skuStyleOrder);
    const name = product?.["display-name"] || "";

    const swatch = {
      selectedSwatchId: filteredBopisSwatches[index]["color-id"],
      selectedSwatchIndex: index,
      previewPhotoImageIndex: index,
    };

    const selectedProductAnalyticsData = {
      name,
      color: filteredBopisSwatches[index]["color-name"],
      skuId: filteredBopisSwatches[index].sku,
      size: filteredBopisSwatches[index].size,
    };

    // Update $window.digitalData.product
    updateProductColorAndSkuId(selectedProductAnalyticsData);

    this.setState(swatch);
  }

  createSizeQueryStr(currentSizeFilters) {
    if (!currentSizeFilters || currentSizeFilters.length === 0) return "";

    return `&sz=${currentSizeFilters.join(",")}`;
  }

  createBopisQueryStr(showBopis) {
    return showBopis && this.props.bopisFulfillmentAB ? `&bp=${showBopis}` : "";
  }

  createSizeUrlQueryStringForColorId(colorId) {
    const { skus, currentSizeFilters } = this.props;

    // Without availability info, fall back to all filtered sizes
    if (skus == null) return this.createSizeQueryStr(currentSizeFilters);

    // Otherwise, use the intersection of available sizes and filtered sizes
    const skusByColorId = groupBy(skus, "colorId")[colorId] ?? [];
    const availableSizes = new Set(skusByColorId.map((sku) => sku.size));
    const sizeIntersection = currentSizeFilters
      .filter((size) => availableSizes.has(size))
      .sort((a, b) => a - b);

    return this.createSizeQueryStr(sizeIntersection);
  }

  callSatelliteTracking = () => {
    const { product } = this.props;
    const { selectedSwatchId } = this.state;

    let displayPrice;
    if (product["product-on-sale"] === "1") {
      displayPrice = Array.isArray(product["product-sale-price"])
        ? product["product-sale-price"][0]
        : product["product-sale-price"];
    } else {
      displayPrice = Array.isArray(product["list-price"])
        ? product["list-price"][0]
        : product["list-price"];
    }

    const skuStyleOrder = product["sku-style-order"] || [];
    const sku = skuStyleOrder.find(
      (sku) => sku["color-id"] === selectedSwatchId
    );
  };

  setIsItemInWishlist(isItemInWishlist) {
    this.setState({ isItemInWishlist });
  }

  /**
    JSX to render the component
  */
  render() {
    const {
      product,
      currentSizeFilters,
      isMobileDevice,
      bp,
      sizePill,
      showHoverImage,
      productIndex,
      showBopis,
      graphqlClient,
      onClick,
      categoryTitle,
      cdpSerpSwatch,
      wishlists,
      productCount,
      productDetail,
      shouldDisplaySelectiveComparison,
      productsToCompare,
      updateProductsToCompare,
      focusComparisonDrawer,
    } = this.props;
    const {
      selectedSwatchId,
      selectedSwatchIndex,
      images,
      swatches,
      previewPhotoImageIndex,
      selectedColor,
      isItemInWishlist,
    } = this.state;

    const productId = product?.["product-id"] || product?.["repository-id"];
    const categoryId = product?.["parent-category-unified-id"];
    const unifiedId = product?.["unified-id"];
    const pdpUrl = product?.["pdp-url"];
    const displayName = product?.["display-name"];

    const frenchPriceAttributes = this.isFrenchLocale
      ? {
          locale: "fr-CA",
          showCurrencyCode: "left",
          currencyCode: "CAD",
        }
      : null;

    const {
      "list-price": listPrice = "",
      "product-on-sale": productOnSale = "",
      "product-sale-price": productSalePrice = [],
    } = product;

    const altText = `View details of ${product["display-name"]}`.replace(
      "*",
      ""
    );

    const isSelfCare = isSelfCareProductTile(product);

    // get correct color-id for product links
    const productColorUrl =
      process.browser && selectedSwatchId
        ? `${product["pdp-url"]}?color=${selectedSwatchId}`
        : product["pdp-url"];

    const sizeUrlQueryString = this.createSizeQueryStr(currentSizeFilters);
    const bopisUrlQueryString = this.createBopisQueryStr(showBopis);
    const urlWithFilters = encodeURI(
      productColorUrl + sizeUrlQueryString + bopisUrlQueryString
    );
    const skuStyle = product["sku-style-order"][selectedSwatchIndex];

    const srcImage = images[previewPhotoImageIndex]?.src;
    const hoverImage = images[previewPhotoImageIndex]?.hover;
    const analyticsProductIndex = productIndex + 1;

    /**
     * Check for product highlights.
     *
     * Kiwi-module-highlights can return highlights for CDP and PDP.
     * Kiwi will filter out the highest priority highlight and return as an array.
     *  Note: CDP also has category highlight exclusions handled in the kiwi layer.
     *
     * If no kiwi-module-highlights are returned, we check a local static list for
     * product highlights.
     *  NOTE: Static highlights will be removed in Q2 '24 after SRE finishes Performance
     *  validation on kiwi-module-highlights.
     *    See: https://lululemon.atlassian.net/browse/COCO-2658
     */
    const highlight = product.highlights?.[0] || {};
    const highlightText = highlight?.["highlight-label"];
    const showProductHighlight = highlight?.["highlight-label"];

    const analyticsProductBadges = showProductHighlight
      ? kebabCase(highlightText)
      : "";

    return (
      <div className="product-tile">
        <LinkGenerator
          className="product-tile__image-link"
          href={urlWithFilters}
          onClick={onClick}
          analyticsAttrs={analyticsProductLinkDataAttributes({
            product,
            skuStyle,
            isBOPIS: showBopis,
            productIndex: analyticsProductIndex,
            productBadges: analyticsProductBadges,
            productDetail: productDetail,
          })}
        >
          <div className="image-container">
            <HoverImage
              src={srcImage}
              hoverSrc={hoverImage}
              prefix="product_tile"
              sizes={sizes}
              alt={altText}
              id={`${product["default-sku"]}_${previewPhotoImageIndex}`}
              isMobileDevice={isMobileDevice}
              showHoverImage={showHoverImage}
            />
          </div>
          {sizePill && (
            <Pill
              className="OneLinkTx product-tile__floating"
              iconProps={{ position: "left", icon: successIcon }}
            >
              {sizePill}
            </Pill>
          )}
        </LinkGenerator>
        <WishlistHeart
          isItemInWishlist={isItemInWishlist}
          isMobile={isMobileDevice}
          graphqlClient={graphqlClient}
          product={product}
          selectedSwatchIndex={selectedSwatchIndex}
          swatches={swatches}
          wishlists={wishlists}
          setIsItemInWishlist={this.setIsItemInWishlist}
          productIndex={productIndex}
          productCount={productCount}
        />
        <div className="product-tile__details">
          <div className="product-color-swatches">
            <div className="slider-container">
              <SwatchCarousel
                analyticsAttrs={analyticsProductLinkDataAttributes({ product })}
                disabledColorIds={this.disabledColorIds}
                swatchList={swatches}
                product={product}
                pdpLink={product["pdp-url"]}
                getSwatchIndex={this.selectSwatch}
                activeSwatch={selectedSwatchIndex}
                selectedColor={selectedSwatchId}
                sizeUrlQueryString={this.createSizeUrlQueryStringForColorId}
                bp={bp}
                productIndex={analyticsProductIndex}
                showBopis={showBopis}
                cdpSerpSwatch={cdpSerpSwatch}
                wishlists={wishlists}
                setIsItemInWishlist={this.setIsItemInWishlist}
              />
            </div>
          </div>
          <ProductHighlight
            product={product}
            page={HIGHLIGHT_TYPE.CDP}
            className="product-tile__badge"
          />
          <div className="product-tile__product-attributes">
            <div className="product-tile__product-attributes__title-and-price">
              <h3 className="product-tile__product-name lll-text-body-1">
                <LinkGenerator
                  className="lll-font-weight-medium"
                  href={urlWithFilters}
                  onClick={onClick}
                  analyticsAttrs={analyticsProductLinkDataAttributes({
                    product,
                    skuStyle,
                    isBOPIS: showBopis,
                    productIndex: analyticsProductIndex,
                    isLabel: true,
                    productBadges: analyticsProductBadges,
                    productDetail: productDetail,
                  })}
                >
                  {insertLineBreakNoHTML(product["display-name"])}
                </LinkGenerator>
              </h3>
              <Price
                prices={stripDecimalFromPrice(listPrice)}
                salePrices={
                  productOnSale ? stripDecimalFromPrice(productSalePrice) : null
                }
                {...frenchPriceAttributes}
              />
            </div>
            {productDetail && (
              <div className="product-tile__product-detail">
                <p className="lll-text-body-3">{productDetail}</p>
              </div>
            )}
            <div className="product-tile__color-count-and-checkbox">
              {!isSelfCare && (
                <p className="product-tile__color-count lll-text-body-3">
                  {colorGroupLength(swatches)}
                </p>
              )}
              <SelectiveComparisonCheckbox
                productId={productId}
                categoryId={categoryId}
                unifiedId={unifiedId}
                pdpUrl={pdpUrl}
                displayName={displayName}
                shouldDisplaySelectiveComparison={
                  shouldDisplaySelectiveComparison
                }
                productsToCompare={productsToCompare}
                updateProductsToCompare={updateProductsToCompare}
                focusComparisonDrawer={focusComparisonDrawer}
                images={images}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ProductTile.propTypes = {
  bp: string,
  onClick: PropTypes.func,
  categoryTitle: PropTypes.string,
  cdpSerpSwatch: PropTypes.bool,
  currentColorFilters: PropTypes.arrayOf(PropTypes.string),
  currentSizeFilters: PropTypes.arrayOf(PropTypes.string),
  isMobileDevice: bool,
  product: shape({
    "all-available-sizes": PropTypes.array,
    "bazaar-voice-id": PropTypes.string,
    "color-group": PropTypes.array,
    "common-id": PropTypes.string,
    "currency-code": PropTypes.string,
    "default-parent-category": PropTypes.string,
    "default-sku": PropTypes.string,
    "display-name": PropTypes.string,
    "display-name-with-br": PropTypes.string,
    "f5-bckimg-url": PropTypes.string,
    "has-linked-products": PropTypes.string,
    highlights: PropTypes.arrayOf(
      shape({
        "highlight-label": PropTypes.string,
        "highlight-icon-web": PropTypes.string,
      })
    ),
    "image-scheme": PropTypes.string,
    "linked-products": PropTypes.array,
    "list-price": PropTypes.array,
    "parent-category-display-name": PropTypes.string,
    "parent-category-unified-id": PropTypes.string,
    "pdp-url": PropTypes.string,
    "product-applicable-systems": PropTypes.string,
    "product-category-hierarchy": PropTypes.string,
    "product-coverage": PropTypes.string,
    "product-on-sale": PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    "product-sale-price": PropTypes.array,
    "product-silver": PropTypes.string,
    "product-whats-new": PropTypes.string,
    "repository-id": PropTypes.string,
    "sku-sku-images": PropTypes.array,
    "sku-style-number": PropTypes.array,
    "sku-style-order": PropTypes.array,
    title: PropTypes.string,
    "unified-id": PropTypes.string,
  }),
  productIndex: number.isRequired,
  productCount: number.isRequired,
  showBopis: bool,
  showHoverImage: bool,
  sizePill: string,
  skus: PropTypes.arrayOf(PropTypes.object),
  productDetail: PropTypes.string,
  bopisFulfillmentAB: PropTypes.bool,
  shouldDisplaySelectiveComparison: PropTypes.bool,
  productsToCompare: PropTypes.arrayOf(PropTypes.object),
  updateProductsToCompare: PropTypes.func,
  focusComparisonDrawer: PropTypes.func,
};

ProductTile.defaultProps = {
  product: {},
  currentColorFilters: [],
  currentSizeFilters: [],
  sizePill: "",
  isMobileDevice: false,
  bp: "desktop",
  onClick: () => {},
  showHoverImage: false,
  showBopis: false,
  cdpSerpSwatch: false,
  productCount: 0,
  productDetail: "",
  bopisFulfillmentAB: false,
  shouldDisplaySelectiveComparison: false,
  productsToCompare: [],
  updateProductsToCompare: () => {},
  focusComparisonDrawer: () => {},
};

export default ProductTile;
